import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import InvoiceStatusChip from '../../components/crm/InvoiceStatusChip';
import FilterChip from '../../components/crm/FilterChip';
import EmptyState from '../../components/crm/EmptyState';
import PaginatedTable from '../../components/admin/PaginatedTable';
import AccountPicker from '../../components/admin/AccountPicker';
import usePaginatedQuery from '../../hooks/usePaginatedQuery';
import { fmtCents, INVOICE_STATUSES } from '../../lib/financial';

// Sprint I M4: status chip labels rendered as Title Case (was raw underscored).
function statusLabel(s) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const DATE_RANGES = [
  { key: '', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7d' },
  { key: '30d', label: 'Last 30d' },
  { key: '90d', label: 'Last 90d' },
  { key: 'ytd', label: 'YTD' },
];

export default function Invoices() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const overdueOnly = searchParams.get('overdue') === 'true';
  const accountId = searchParams.get('account_id') || '';
  const dateRange = searchParams.get('range') || '';
  // ?aging=31-60 arrives from the dashboard / Financial AR bucket tiles.
  const aging = searchParams.get('aging') || '';

  const updateParams = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      }
      next.delete('offset');
      return next;
    });
  };

  const paginator = usePaginatedQuery({
    path: '/api/admin/financial/invoices',
    responseKey: 'invoices',
    extraParams: {
      status,
      overdue: overdueOnly ? 'true' : '',
      account_id: accountId,
      range: dateRange,
      aging,
    },
  });

  const anyFilterSet = !!status || overdueOnly || !!accountId || !!dateRange || !!aging;

  // Sprint K — invoices opened to staff (admin + ops). Aggregate financial
  // dashboards remain admin-only via /admin/financial route gate.
  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" description="Invoice views are restricted to NexSav staff." />;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Invoices</span></h1>
        <p className="text-[13px] text-gray-500 mt-0.5">All invoices across all accounts.</p>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <FilterChip active={!anyFilterSet} onClick={() => updateParams({ status: null, overdue: null, account_id: null, range: null, aging: null })}>All</FilterChip>
        {INVOICE_STATUSES.map((s) => (
          <FilterChip key={s} active={status === s} onClick={() => updateParams({ status: s, overdue: null })}>{statusLabel(s)}</FilterChip>
        ))}
        <FilterChip active={overdueOnly} onClick={() => updateParams({ overdue: true, status: null })}>Overdue</FilterChip>
        {aging && (
          <button
            type="button"
            onClick={() => updateParams({ aging: null })}
            data-testid="aging-filter-chip"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] bg-[#B0FC38]/15 border border-[#B0FC38]/30 text-[#B0FC38] hover:bg-[#B0FC38]/25"
            title="Remove this filter"
          >
            Outstanding {aging === '90+' ? 'over 90' : aging} days
            <X className="w-3 h-3" aria-hidden="true" />
            <span className="sr-only">Remove ageing filter</span>
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div data-testid="invoices-account-filter" className="min-w-[260px] flex-1 max-w-md">
          <AccountPicker
            value={accountId}
            onChange={(id) => updateParams({ account_id: id || null })}
            placeholder="Filter by account…"
          />
        </div>
        <select
          data-testid="invoices-range-filter"
          value={dateRange}
          onChange={(e) => updateParams({ range: e.target.value || null })}
          className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-1.5 text-[12px] text-white"
        >
          {DATE_RANGES.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
      </div>

      <PaginatedTable
        paginator={paginator}
        anyFilterSet={anyFilterSet}
        emptyMessage="No invoices yet."
        emptyFilteredMessage="No invoices match these filters."
        renderTable={(rows) => (
          <div className="rounded-xl border border-white/[0.05] bg-white/[0.01] overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-white/[0.02] border-b border-white/[0.05] text-[10px] uppercase tracking-wider text-gray-500">
              <div className="col-span-5 lg:col-span-3">Number</div>
              <div className="col-span-4 lg:col-span-2">Status</div>
              <div className="col-span-3 lg:col-span-2 text-right">Amount</div>
              <div className="hidden lg:block lg:col-span-2 text-right">Outstanding</div>
              <div className="hidden lg:block lg:col-span-3">Due</div>
            </div>
            {rows.map((inv) => (
              <button key={inv.id} onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                className="w-full grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/[0.03] hover:bg-white/[0.02] text-left">
                <div className="col-span-5 lg:col-span-3 text-[13px] text-white font-mono truncate">{inv.invoiceNumber}</div>
                <div className="col-span-4 lg:col-span-2 flex items-center gap-2">
                  <InvoiceStatusChip status={inv.displayStatus || inv.status} />
                  {inv.isOverdue && <span className="text-[10px] text-red-300 uppercase tracking-wider">overdue</span>}
                </div>
                <div className="col-span-3 lg:col-span-2 text-right text-[12px] text-gray-300">{fmtCents(inv.amountCents, inv.currency)}</div>
                <div className="hidden lg:block lg:col-span-2 text-right text-[12px] text-gray-400">{fmtCents(inv.outstandingCents, inv.currency)}</div>
                <div className="hidden lg:block lg:col-span-3 text-[12px] text-gray-500">{inv.dueAt || '—'}</div>
              </button>
            ))}
          </div>
        )}
      />
    </div>
  );
}
